import React from 'react';
import magGlassIcon from 'assets/images/icons/magnifying-glass.svg';
import Spinner from 'components/Spinner';
import { Vehicle } from 'types/expiryRenewal';
import useAddVehicle from 'views/MyRac/ExpiryRenewal/hooks/useAddVehicle';
import useEditVehicle from 'views/MyRac/ExpiryRenewal/hooks/useEditVehicle';
import ModalButton from 'components/ModalButton/ModalButton';
import CommonLargeModal from 'components/Modals/CommonLargeModal';
import TextInput from '../../atoms/TextInput/TextInput';
import styles from './VehicleModal.module.scss';

type VehicleFormProps = Omit<ReturnType<typeof useAddVehicle>, 'clearRegistrationSearchError'> & {
  title: string;
  submitLabel: string;
  onClose: () => void;
  onSearchErrorDismiss?: () => void;
};

const VehicleForm: React.FC<VehicleFormProps> = ({
  title,
  submitLabel,
  onClose,
  onSearchErrorDismiss,
  fields,
  errors,
  touched,
  loading,
  isSaveDisabled,
  isVehicleNotFound,
  searchAndRegError,
  handleChange,
  handleBlur,
  handleSearch,
  handleSave,
}) => (
  <CommonLargeModal
    show
    onHide={onClose}
    title={<span style={{ color: '#47474A' }}>{title}</span>}
    titleClassName="font-bold-display fw-normal fs-3"
    bodyClassName=""
    bodyStyle={{}}
  >
    {/* The form owns the content column so the inputs get the full width of it. */}
    <form noValidate onSubmit={handleSave} style={{ margin: '0 auto', maxWidth: '510px' }}>
      <TextInput
        name="Registration"
        label="Number plate"
        value={fields.Registration}
        error={touched.Registration && errors.Registration ? errors.Registration : undefined}
        maxLength={8}
        onChange={handleChange}
        onBlur={() => handleBlur('Registration')}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
          }
        }}
        rightElement={(
          <button
            type="button"
            aria-label="Search vehicle registration"
            onClick={handleSearch}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}
          >
            {loading ? <Spinner buttonSpinner /> : <img src={magGlassIcon} alt="Search" />}
          </button>
        )}
      >
        {/*
          A failed DVLA lookup is clickable - the member can carry on with the
          registration they typed. Any other registration message (a duplicate, say)
          is just text, and the dismissible prompt wins when both are set.
        */}
        {isVehicleNotFound && onSearchErrorDismiss
          ? (
            <button
              type="button"
              className={styles.searchErrorDismiss}
              onClick={onSearchErrorDismiss}
            >
              Sorry, we could not find that vehicle. Please try again or <u>click this message</u> to continue with this registration.
            </button>
          )
          : searchAndRegError && <p className={styles.searchError}>{searchAndRegError}</p>}
      </TextInput>

      <TextInput
        name="Make"
        label="Make"
        value={fields.Make}
        error={touched.Make && errors.Make ? 'Required' : undefined}
        maxLength={50}
        onChange={handleChange}
        onBlur={() => handleBlur('Make')}
      />

      <TextInput
        name="Model"
        label="Model"
        value={fields.Model}
        error={touched.Model && errors.Model ? 'Required' : undefined}
        maxLength={50}
        onChange={handleChange}
        onBlur={() => handleBlur('Model')}
      />

      <TextInput
        name="YearOfManufacture"
        label="Manufactured year"
        value={fields.YearOfManufacture}
        error={touched.YearOfManufacture ? errors.YearOfManufacture || undefined : undefined}
        type="number"
        maxLength={4}
        onChange={handleChange}
        onBlur={() => handleBlur('YearOfManufacture')}
      />

      <ModalButton type="submit" variant="filled" disabled={isSaveDisabled}>
        {submitLabel}
      </ModalButton>

      <ModalButton type="button" variant="outline" onClick={onClose}>
        Cancel
      </ModalButton>
    </form>
  </CommonLargeModal>
);

interface VehicleAddModalProps {
  mode: 'add';
  onClose: () => void;
}

interface VehicleEditModalProps {
  mode: 'edit';
  vehicle: Vehicle;
  onClose: () => void;
}

export type VehicleModalProps = VehicleAddModalProps | VehicleEditModalProps;

const VehicleAddModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { fields, errors, touched, loading, isSaveDisabled, searchAndRegError, isVehicleNotFound, clearRegistrationSearchError, handleChange, handleBlur, handleSearch, handleSave } = useAddVehicle(onClose);
  return (
    <VehicleForm
      title="Enter your vehicle details"
      submitLabel="Add vehicle"
      onClose={onClose}
      onSearchErrorDismiss={clearRegistrationSearchError}
      fields={fields}
      errors={errors}
      touched={touched}
      loading={loading}
      isSaveDisabled={isSaveDisabled}
      isVehicleNotFound={isVehicleNotFound}
      searchAndRegError={searchAndRegError}
      handleChange={handleChange}
      handleBlur={handleBlur}
      handleSearch={handleSearch}
      handleSave={handleSave}
    />
  );
};

const VehicleEditModal: React.FC<{ vehicle: Vehicle; onClose: () => void }> = ({ vehicle, onClose }) => {
  const { fields, errors, touched, loading, isSaveDisabled, searchAndRegError, isVehicleNotFound, clearRegistrationSearchError, handleChange, handleBlur, handleSearch, handleSave } = useEditVehicle(vehicle, onClose);
  return (
    <VehicleForm
      title="Edit your vehicle details"
      submitLabel="Save vehicle"
      onClose={onClose}
      onSearchErrorDismiss={clearRegistrationSearchError}
      fields={fields}
      errors={errors}
      touched={touched}
      loading={loading}
      isSaveDisabled={isSaveDisabled}
      isVehicleNotFound={isVehicleNotFound}
      searchAndRegError={searchAndRegError}
      handleChange={handleChange}
      handleBlur={handleBlur}
      handleSearch={handleSearch}
      handleSave={handleSave}
    />
  );
};

const VehicleModal: React.FC<VehicleModalProps> = ({ mode, onClose, ...rest }) => {
  if (mode === 'add') {
    return <VehicleAddModal onClose={onClose} />;
  }
  const { vehicle } = rest as VehicleEditModalProps;
  return <VehicleEditModal vehicle={vehicle} onClose={onClose} />;
};

export default VehicleModal;
